/**
 * Generation History — the factory's memory of everything it has accepted.
 *
 * Duplicate detection needs to compare each candidate against prior output,
 * and that must stay fast at 100k+ templates:
 *   - exact clones: O(1) via a fingerprint Set
 *   - near-duplicates: similarity scans are bucketed per category, and each
 *     scan is capped (recent records are the most likely collisions — a
 *     candidate colliding with template #90,000 looks like one colliding
 *     with #100,000)
 *
 * Persistence is optional and capped: pass { storageKey } to keep the most
 * recent records in localStorage across page reloads. The in-memory store
 * is the source of truth; storage is best-effort.
 */

export const DEFAULT_CAPACITY = 100000;
const PERSIST_LIMIT = 1000;
const DEFAULT_SCAN_LIMIT = 5000;

export class GenerationHistory {
  constructor({ capacity = DEFAULT_CAPACITY, storageKey = null } = {}) {
    this.capacity = capacity;
    this.storageKey = storageKey;
    this.records = []; // { id, category, dna, fingerprint, score, createdAt }
    this.fingerprints = new Set();
    this.byCategory = new Map();
    if (storageKey) this._load();
  }

  get size() {
    return this.records.length;
  }

  hasFingerprint(fp) {
    return this.fingerprints.has(fp);
  }

  /**
   * DNA records to compare a new candidate against — same-category records,
   * most recent first, capped so a scan never becomes the bottleneck.
   */
  candidatesFor(category, limit = DEFAULT_SCAN_LIMIT) {
    const bucket = this.byCategory.get(category) || [];
    return bucket.slice(-limit).map((r) => r.dna);
  }

  add({ category, dna, fingerprint, score }) {
    const record = {
      id: `tpl_${this.records.length + 1}_${Date.now().toString(36)}`,
      category, dna, fingerprint, score,
      createdAt: new Date().toISOString(),
    };
    this.records.push(record);
    this.fingerprints.add(fingerprint);
    if (!this.byCategory.has(category)) this.byCategory.set(category, []);
    this.byCategory.get(category).push(record);

    if (this.records.length > this.capacity) this._evictOldest();
    this._save();
    return record;
  }

  all() {
    return [...this.records];
  }

  clear() {
    this.records = [];
    this.fingerprints.clear();
    this.byCategory.clear();
    this._save();
  }

  _evictOldest() {
    const evicted = this.records.shift();
    this.fingerprints.delete(evicted.fingerprint);
    const bucket = this.byCategory.get(evicted.category);
    if (bucket?.[0]?.id === evicted.id) bucket.shift();
  }

  _load() {
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) return;
      const records = JSON.parse(raw);
      records.forEach((r) => {
        this.records.push(r);
        this.fingerprints.add(r.fingerprint);
        if (!this.byCategory.has(r.category)) this.byCategory.set(r.category, []);
        this.byCategory.get(r.category).push(r);
      });
    } catch { /* corrupt or unavailable storage — start empty */ }
  }

  _save() {
    if (!this.storageKey) return;
    try {
      window.localStorage.setItem(
        this.storageKey,
        JSON.stringify(this.records.slice(-PERSIST_LIMIT))
      );
    } catch { /* quota or unavailable storage — memory still holds truth */ }
  }
}
